/**
 * Helpers for the `{top_right, bottom_right, bottom_left, top_left}` bounds
 * shape used by the gardens API and the Vuex map module.
 *
 * Shape conversion helpers (NE/SW -> TRBRBLTL, TLBR -> TRBRBLTL, array form,
 * Google-Maps Circle -> bounds) live in `services/location.js` for now.
 * This module currently only owns the serialiser used by the gardens
 * `filter[geo_bounding_box]` parameter and the `search_bounds` URL query.
 */

/**
 * Serialise a TRBRBLTL bounds object into the comma-delimited
 * `top_right.lat,top_right.lng,bottom_left.lat,bottom_left.lng` string
 * that both `filter[geo_bounding_box]` and the URL `search_bounds`
 * parameter expect.
 *
 * Returns null when the input is missing the required corners.
 *
 * @param {{ top_right?: {lat: number, lng: number}, bottom_left?: {lat: number, lng: number} } | null | undefined} bounds
 * @returns {string|null}
 */
export function boundsToQueryString(bounds) {
  const tr = bounds?.top_right
  const bl = bounds?.bottom_left
  if (!tr || !bl) return null
  if (!Number.isFinite(tr.lat) || !Number.isFinite(tr.lng)) return null
  if (!Number.isFinite(bl.lat) || !Number.isFinite(bl.lng)) return null
  return `${tr.lat},${tr.lng},${bl.lat},${bl.lng}`
}

const METRES_PER_DEGREE_LAT = 111320

/**
 * TRBRBLTL bbox that contains a circle. Used so the map can fit the
 * county-search circle overlay without destroying the map slot.
 *
 * Latitude span: `radiusMeters / METRES_PER_DEGREE_LAT`.
 * Longitude span at the circle's latitude scales by `cos(lat)` since
 * meridians converge toward the poles; the standard correction is
 * accurate enough for UK-sized circles.
 *
 * Returns null when the inputs are missing or non-finite.
 *
 * @param {{lat:number, lng:number}} center
 * @param {number} radiusMeters
 * @returns {object|null}
 */
export function bboxFromCircle(center, radiusMeters) {
  if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return null
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return null

  const latDelta = radiusMeters / METRES_PER_DEGREE_LAT
  const cosLat = Math.cos((center.lat * Math.PI) / 180)
  // Guard against the poles where cosLat -> 0 (not realistic for UK but
  // cheap to defend against). Treat the lng span as the lat span there.
  const lngDelta = cosLat > 1e-6 ? radiusMeters / (METRES_PER_DEGREE_LAT * cosLat) : latDelta
  const north = center.lat + latDelta
  const south = center.lat - latDelta
  const east = center.lng + lngDelta
  const west = center.lng - lngDelta

  return {
    top_right: { lat: north, lng: east },
    bottom_right: { lat: south, lng: east },
    bottom_left: { lat: south, lng: west },
    top_left: { lat: north, lng: west },
  }
}

/**
 * TRBRBLTL bbox of a Google-Maps-style ring (`[{lat, lng}, ...]`). Used
 * to fit the map to a polygon overlay (e.g. a buffered county outline).
 *
 * Returns null when the input is empty or contains no finite coords.
 *
 * @param {Array<{lat:number, lng:number}>|null|undefined} paths
 * @returns {object|null}
 */
export function bboxFromPaths(paths) {
  if (!Array.isArray(paths) || paths.length === 0) return null
  let north = -Infinity
  let south = Infinity
  let east = -Infinity
  let west = Infinity
  for (let i = 0; i < paths.length; i += 1) {
    const p = paths[i]
    if (p && Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
      if (p.lat > north) north = p.lat
      if (p.lat < south) south = p.lat
      if (p.lng > east) east = p.lng
      if (p.lng < west) west = p.lng
    }
  }
  if (!Number.isFinite(north) || !Number.isFinite(south)
    || !Number.isFinite(east) || !Number.isFinite(west)) return null
  return {
    top_right: { lat: north, lng: east },
    bottom_right: { lat: south, lng: east },
    bottom_left: { lat: south, lng: west },
    top_left: { lat: north, lng: west },
  }
}

/**
 * Inflate a TRBRBLTL bounds outward by `delta` degrees in all four
 * directions. Used to widen a postcode's tight Google viewport so the
 * gardens search picks up nearby pins.
 *
 * Each corner moves away from the centre by `delta`:
 *   - top_right    (NE): lat +, lng +
 *   - bottom_right (SE): lat -, lng +
 *   - bottom_left  (SW): lat -, lng -
 *   - top_left     (NW): lat +, lng -
 *
 * Returns a new object; the input is not mutated.
 *
 * @param {object} bounds  TRBRBLTL bounds.
 * @param {number} delta   Padding in decimal degrees.
 * @returns {object}
 */
export function padBoundsOutward(bounds, delta) {
  return {
    top_right: {
      lat: bounds.top_right.lat + delta,
      lng: bounds.top_right.lng + delta,
    },
    bottom_right: {
      lat: bounds.bottom_right.lat - delta,
      lng: bounds.bottom_right.lng + delta,
    },
    bottom_left: {
      lat: bounds.bottom_left.lat - delta,
      lng: bounds.bottom_left.lng - delta,
    },
    top_left: {
      lat: bounds.top_left.lat + delta,
      lng: bounds.top_left.lng - delta,
    },
  }
}
